import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Camera, X } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import BeforeAfter from "@/components/BeforeAfter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOTS = ["Interior Deep Clean", "Exterior Wash & Wheels", "Full Detail"];
const imgUrl = (id, which) => `${API}/gallery/image/${id}/${which}`;

const Gallery = () => {
  const [photos, setPhotos] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    fetch(`${API}/gallery`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setPhotos(d.photos || []))
      .catch(() => setPhotos([]));
  }, []);

  const hasPhotos = photos && photos.length > 0;

  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="gallery-section">
      <SectionHeading
        index="06"
        eyebrow="Real Results"
        title="See What a Thorough Detail Can Do"
        sub={
          hasPhotos
            ? "Real before-and-after photos from actual customer vehicles, shared with permission. Drag the slider to compare."
            : "We're building this wall with real before-and-after photos from actual customer vehicles — shared with permission. No stock photos passed off as our work."
        }
      />
      {hasPhotos ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {photos.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.06} y={28}>
              <figure className="border border-white/10 bg-surface" data-testid={`gallery-photo-${i}`}>
                <button onClick={() => setOpen(p)} className="block w-full text-left" aria-label={`${p.label} — open larger before and after`}>
                  <BeforeAfter before={imgUrl(p.id, "before")} after={imgUrl(p.id, "after")} alt={p.label} className="aspect-[4/3] w-full" />
                </button>
                <figcaption className="flex items-center justify-between px-4 py-3">
                  <span className="font-display text-lg font-semibold uppercase tracking-wide text-white">{p.label}</span>
                  <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.2em] text-slate-500">Drag to compare</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      ) : (
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        {SLOTS.map((label, i) => (
          <Reveal key={label} delay={i * 0.08} y={28}>
            <button
              onClick={() => setOpen(label)}
              className="group flex aspect-[4/3] w-full flex-col items-center justify-center gap-4 border border-dashed border-white/20 bg-surface transition-colors duration-300 hover:border-blue-500/60 hover:bg-panel"
              data-testid={`gallery-slot-${i}`}
              aria-label={`${label} — photo coming soon, open details`}
            >
              <span className="flex h-14 w-14 items-center justify-center border border-white/15 text-slate-500 transition-colors duration-300 group-hover:border-blue-500/50 group-hover:text-blue-400">
                <Camera size={24} strokeWidth={1.6} aria-hidden="true" />
              </span>
              <span className="font-display text-lg font-semibold uppercase tracking-wide text-slate-300">{label}</span>
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
                Before / After — Coming Soon
              </span>
            </button>
          </Reveal>
        ))}
      </div>
      )}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-obsidian/90 p-5 backdrop-blur-sm"
            onClick={() => setOpen(null)}
            role="dialog"
            aria-modal="true"
            data-testid="gallery-lightbox"
          >
            <motion.div
              initial={{ scale: 0.94, y: 16 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 16 }}
              className={`relative w-full border border-white/15 bg-panel ${typeof open === "string" ? "max-w-md p-10 text-center" : "max-w-3xl p-3 sm:p-4"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => setOpen(null)} className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center border border-white/15 bg-obsidian/80 text-slate-400 hover:text-white" aria-label="Close" data-testid="gallery-lightbox-close">
                <X size={17} />
              </button>
              {typeof open === "string" ? (
                <>
                  <Camera size={30} strokeWidth={1.5} className="mx-auto text-blue-500" aria-hidden="true" />
                  <h3 className="mt-5 font-display text-2xl font-bold uppercase tracking-wide text-white">{open}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">
                    Real customer before-and-after photos will live here as jobs are completed. Every photo shown will be
                    actual work from Freedom Mobile Auto Detailing — never stock imagery.
                  </p>
                </>
              ) : (
                <>
                  <BeforeAfter before={imgUrl(open.id, "before")} after={imgUrl(open.id, "after")} alt={open.label} className="aspect-[4/3] w-full sm:aspect-[16/10]" />
                  <p className="px-2 py-3 text-center font-display text-xl font-semibold uppercase tracking-wide text-white">{open.label}</p>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
